import React, {useState, useEffect} from 'react'
import {Modal} from 'react-bootstrap'
import {Button, Select, TextInput, Textarea} from '@mantine/core'
import {toast} from 'react-toastify'
import {
  getTaskTypes,
  createTaskType,
  updateTaskType,
  deleteTaskType,
  activateTaskType,
  getTaskTypeCategories,
  TaskType,
  TaskTypeInput,
} from '../../../api/TimesheetAPI'
import {PageHeader} from '../../components/PageHeader/PageHeader'
import {DataTable} from '../../components/DataTable/DataTable'
import {ActionButtons} from '../../components/ActionButtons/ActionButtons'
import {StatusBadge} from '../../components/StatusBadge/StatusBadge'
import {FormWrapper} from '../../components/FormWrapper/FormWrapper'
import {useApiCall} from '../../hooks/useApiCall'

const TaskTypes: React.FC = () => {
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([])
  const [filteredTaskTypes, setFilteredTaskTypes] = useState<TaskType[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingTaskType, setEditingTaskType] = useState<TaskType | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>('ALL')
  const [statusFilter, setStatusFilter] = useState<string>('ALL')
  const [searchTerm, setSearchTerm] = useState<string>('')

  const {loading, error, execute} = useApiCall<any>()

  const [formData, setFormData] = useState<TaskTypeInput>({
    name: '',
    category: '',
    description: '',
    isActive: true,
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  // Load task types and categories
  const loadTaskTypes = async () => {
    await execute(async () => {
      const response = await getTaskTypes()
      setTaskTypes(response.data.data)
      return response.data.data
    })
  }

  const loadCategories = async () => {
    try {
      const response = await getTaskTypeCategories()
      setCategories(response.data.data)
    } catch (err) {
      console.error('Failed to load categories:', err)
    }
  }

  useEffect(() => {
    loadTaskTypes()
    loadCategories()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Apply filters
  useEffect(() => {
    let filtered = taskTypes

    if (categoryFilter !== 'ALL') {
      filtered = filtered.filter((taskType) => taskType.category === categoryFilter)
    }

    if (statusFilter === 'ACTIVE') {
      filtered = filtered.filter((taskType) => taskType.isActive)
    } else if (statusFilter === 'INACTIVE') {
      filtered = filtered.filter((taskType) => !taskType.isActive)
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (taskType) =>
          taskType.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          taskType.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          taskType.description?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    setFilteredTaskTypes(filtered)
  }, [taskTypes, categoryFilter, statusFilter, searchTerm])

  const openModal = (taskType?: TaskType) => {
    if (taskType) {
      setEditingTaskType(taskType)
      setFormData({
        name: taskType.name,
        category: taskType.category || '',
        description: taskType.description || '',
        isActive: taskType.isActive,
      })
    } else {
      setEditingTaskType(null)
      setFormData({
        name: '',
        category: '',
        description: '',
        isActive: true,
      })
    }
    setFormErrors({})
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingTaskType(null)
    setFormData({
      name: '',
      category: '',
      description: '',
      isActive: true,
    })
    setFormErrors({})
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.name.trim()) {
      errors.name = 'Task type name is required'
    } else if (formData.name.length < 2) {
      errors.name = 'Task type name must be at least 2 characters'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    await execute(async () => {
      if (editingTaskType) {
        const response = await updateTaskType(editingTaskType.id, formData)
        toast.success('Task type updated successfully')
        return response.data
      } else {
        const response = await createTaskType(formData)
        toast.success('Task type created successfully')
        return response.data
      }
    })

    closeModal()
    loadTaskTypes()
    loadCategories()
  }

  const handleDelete = async (taskType: TaskType) => {
    if (!window.confirm(`Are you sure you want to delete "${taskType.name}"?`)) {
      return
    }

    await execute(async () => {
      const response = await deleteTaskType(taskType.id)
      toast.success(response.data.message || 'Task type deleted successfully')
      return response.data
    })

    loadTaskTypes()
  }

  const handleActivate = async (taskType: TaskType) => {
    await execute(async () => {
      const response = await activateTaskType(taskType.id)
      toast.success('Task type activated successfully')
      return response.data
    })

    loadTaskTypes()
  }

  const columns = [
    {
      header: 'Task Type',
      cell: (row: TaskType) => <strong>{row.name}</strong>,
    },
    {
      header: 'Category',
      cell: (row: TaskType) => {
        const categoryColors: Record<string, string> = {
          Technical: 'badge-light-primary',
          'Non-Technical': 'badge-light-info',
          CAE: 'badge-light-success',
          HR: 'badge-light-warning',
          Leaves: 'badge-light-danger',
          General: 'badge-light-secondary',
        }
        const colorClass = categoryColors[row.category || ''] || 'badge-light-secondary'
        return <span className={`badge ${colorClass}`}>{row.category || 'Uncategorized'}</span>
      },
    },
    {
      header: 'Description',
      cell: (row: TaskType) => <span className='text-muted'>{row.description || '-'}</span>,
    },
    {
      header: 'Status',
      cell: (row: TaskType) => <StatusBadge status={row.isActive ? 'Active' : 'Inactive'} />,
    },
    {
      header: 'Actions',
      cell: (row: TaskType) => (
        <div className='d-flex gap-2'>
          <ActionButtons
            actions={[
              {
                type: 'edit',
                onClick: () => openModal(row),
                label: 'Edit',
              },
              ...(row.isActive ? [{
                type: 'delete' as const,
                onClick: () => handleDelete(row),
                label: 'Delete',
                confirm: {
                  title: 'Delete Task Type',
                  text: `Are you sure you want to delete "${row.name}"?`,
                  icon: 'warning' as const,
                },
              }] : []),
            ]}
          />
          {!row.isActive && (
            <Button size='xs' color='green' onClick={() => handleActivate(row)}>
              Activate
            </Button>
          )}
        </div>
      ),
    },
  ]

  return (
    <>
      <div className='card card-flush shadow-sm'>
        <div className='card-header'>
          <h3 className='card-title'>Task Types Management</h3>
          <div className='card-toolbar'>
            <Button onClick={() => openModal()}>
              Add Task Type
            </Button>
          </div>
        </div>
        <div className='card-body'>
        {/* Filters */}
        {/* <div className='row mb-4'>
          <div className='col-md-3'>
            <div className='position-relative'>
              <TextInput
                placeholder='Search task types...'
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className='col-md-3'>
            <Select
              label='Category'
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value || 'ALL')}
              data={[
                {value: 'ALL', label: 'All Categories'},
                ...categories.map((cat) => ({value: cat, label: cat})),
              ]}
            />
          </div>
          <div className='col-md-3'>
            <Select
              label='Status'
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'ALL')}
              data={[
                {value: 'ALL', label: 'All Status'},
                {value: 'ACTIVE', label: 'Active Only'},
                {value: 'INACTIVE', label: 'Inactive Only'},
              ]}
            />
          </div>
          <div className='col-md-3 d-flex align-items-end'>
            <div className='text-muted'>
              Showing {filteredTaskTypes.length} of {taskTypes.length} task types
            </div>
          </div>
        </div> */}
        {/* Filters */}
        <div className="row mb-4 align-items-end">
          {/* Search */}
          <div className="col-md-4">
            <TextInput
              label="Search"
              placeholder="Search task types..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Category */}
          <div className="col-md-4">
            <Select
              label="Category"
              value={categoryFilter}
              onChange={(value) => setCategoryFilter(value || 'ALL')}
              data={[
                { value: 'ALL', label: 'All Categories' },
                ...categories.map((cat) => ({ value: cat, label: cat })),
              ]}
              withinPortal={false}
            />
          </div>

          {/* Status */}
          <div className="col-md-4">
            <Select
              label="Status"
              value={statusFilter}
              onChange={(value) => setStatusFilter(value || 'ALL')}
              data={[
                { value: 'ALL', label: 'All Status' },
                { value: 'ACTIVE', label: 'Active Only' },
                { value: 'INACTIVE', label: 'Inactive Only' },
              ]}
              withinPortal={false}
            />
          </div>
        </div>


        {/* Error Display */}
        {error && (
          <div className='alert alert-danger' role='alert'>
            {error}
          </div>
        )}

        {/* Table */}
        <DataTable
          data={filteredTaskTypes}
          columns={columns}
          loading={loading}
          emptyMessage='No task types found. Click "Add Task Type" to create one.'
        />
        </div>
      </div>

      {/* Add/Edit Modal */}
      {/* <Modal show={isModalOpen} onHide={closeModal} size='lg'>
        <FormWrapper
          title={editingTaskType ? 'Edit Task Type' : 'Add Task Type'}
          show={isModalOpen}
          onSubmit={handleSubmit}
          onCancel={closeModal}
          loading={loading}
        >
          <TextInput
            label='Task Type Name'
            placeholder='e.g., System Issue, Styling, CAD Assembly'
            value={formData.name}
            onChange={(e) => setFormData({...formData, name: e.target.value})}
            error={formErrors.name}
            required
          />

          <Select
            label='Category'
            placeholder='Select or type a category'
            value={formData.category}
            onChange={(value) => setFormData({...formData, category: value || ''})}
            data={categories.map((cat) => ({value: cat, label: cat}))}
            searchable
            creatable
            getCreateLabel={(query) => `+ Create "${query}"`}
            onCreate={(query) => {
              setCategories([...categories, query])
              setFormData({...formData, category: query})
              return query
            }}
            mt='md'
          />

          <Textarea
            label='Description'
            placeholder='Brief description of this task type'
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            rows={3}
            mt='md'
          />

          {editingTaskType && (
            <Select
              label='Status'
              value={formData.isActive ? 'true' : 'false'}
              onChange={(value) => setFormData({...formData, isActive: value === 'true'})}
              data={[
                {value: 'true', label: 'Active'},
                {value: 'false', label: 'Inactive'},
              ]}
              mt='md'
            />
          )}
        </FormWrapper>
      </Modal> */}
      
      {/* Add/Edit Modal */}
      <FormWrapper
        title={editingTaskType ? 'Edit Task Type' : 'Add Task Type'}
        show={isModalOpen}
        onSubmit={handleSubmit}
        onCancel={closeModal}
        loading={loading}
      >
        <TextInput
          label='Task Type Name'
          placeholder='e.g., System Issue, Styling, CAD Assembly'
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          error={formErrors.name}
          required
        />

        <Select
          label='Category'
          placeholder='Select or type a category'
          value={formData.category}
          onChange={(value) => setFormData({...formData, category: value || ''})}
          data={categories.map((cat) => ({value: cat, label: cat}))}
          searchable
          creatable
          getCreateLabel={(query) => `+ Create "${query}"`}
          onCreate={(query) => {
            setCategories([...categories, query])
            setFormData({...formData, category: query})
            return query
          }}
          mt='md'
          withinPortal={false}
        />

        <Textarea
          label='Description'
          placeholder='Brief description of this task type'
          value={formData.description}
          onChange={(e) =>
            setFormData({...formData, description: e.target.value})
          }
          rows={3}
          mt='md'
        />

        <Select
          label='Status'
          value={formData.isActive ? 'true' : 'false'}
          onChange={(value) =>
            setFormData({...formData, isActive: value === 'true'})
          }
          data={[
            {value: 'true', label: 'Active'},
            {value: 'false', label: 'Inactive'},
          ]}
          mt='md'
          withinPortal={false}
        />
      </FormWrapper>
    </>
  )
}

export default TaskTypes

